import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { useActiveBrand } from "../hooks/useActiveBrand";
import { useRuns } from "../hooks/useRuns";
import { OperatorBrandGate } from "../components/layout/OperatorBrandGate";
import { NonWorkspacePage, PageEmptyState, PageErrorState, PageListRow, PageLoadingState } from "../components/layout/NonWorkspacePage";
import { buildBrandScopedPath } from "../lib/brand-navigation";
import { buildWorkspaceHandoffPath, workspaceReturnLocationState } from "../lib/workspace-return";
import { Badge } from "../components/ui/Badge";
import { timeAgo } from "../lib/utils";
import { taskRunJobLabel } from "../lib/task-run-presentation";
import { runOutcome, runOutcomeBadgeVariant } from "../lib/run-outcome";
import type { TaskRunListItem } from "../../../shared/types";

type SortOrder = "newest" | "oldest";
export function activityState(run: TaskRunListItem): string {
  return runOutcome(run).label;
}

export function activityUpdate(run: TaskRunListItem): string {
  return runOutcome(run).description;
}

function dayLabel(value: string): string {
  const d = new Date(value),
    now = new Date();
  const days = Math.floor(
    (Date.UTC(now.getFullYear(), now.getMonth(), now.getDate()) -
      Date.UTC(d.getFullYear(), d.getMonth(), d.getDate())) /
      86400000,
  );
  return days === 0
    ? "Today"
    : days === 1
      ? "Yesterday"
      : d.toLocaleDateString(undefined, { month: "long", day: "numeric" });
}

export function Runs() {
  const { activeBrand, activeBrandId } = useActiveBrand();
  const brandId = activeBrandId ?? activeBrand?.id;
  const { data, isLoading, error } = useRuns(brandId);
  const [query, setQuery] = useState("");
  const [sort, setSort] = useState<SortOrder>("newest");
  useEffect(() => {
    document.title = "Runs — HelloConvo";
  }, []);
  const groups = useMemo(() => {
    const needle = query.trim().toLowerCase();
    const rows = (data?.data ?? []).filter(
      (r) => !needle || taskRunJobLabel(r).toLowerCase().includes(needle),
    );
    rows.sort((a, b) =>
      sort === "newest"
        ? new Date(b.started_at).getTime() - new Date(a.started_at).getTime()
        : new Date(a.started_at).getTime() - new Date(b.started_at).getTime(),
    );
    return rows.reduce<Record<string, TaskRunListItem[]>>((out, r) => {
      const label = dayLabel(r.started_at);
      (out[label] ??= []).push(r);
      return out;
    }, {});
  }, [data?.data, query, sort]);
  if (!brandId)
    return (
      <OperatorBrandGate
        title="Choose a brand before viewing Runs"
        description="Each brand has its own Runs history."
      />
    );
  return (
    <NonWorkspacePage
      width="wide"
      header={{
        eyebrow: "Runs",
        title: "Runs",
        description: "Find recent Action runs and open the Result you need.",
        breadcrumbs: [
          { label: "Workspace", href: "/home" },
          { label: "Runs" },
        ]
      }}
      toolbar={<div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
        <input
          aria-label="Search Runs"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          placeholder="Search work…"
          className="w-full rounded-xl border border-slate-300 px-4 py-2.5 text-sm sm:max-w-md"
        />
        <label className="text-sm text-slate-600">
          Sort:{" "}
          <select
            value={sort}
            onChange={(e) => setSort(e.target.value as SortOrder)}
            className="rounded-lg border border-slate-300 bg-white px-2 py-2"
          >
            <option value="newest">Newest</option>
            <option value="oldest">Oldest</option>
          </select>
        </label>
      </div>}
    >
      {isLoading ? <PageLoadingState>Loading Runs…</PageLoadingState> : error ? (
        <PageErrorState description="We could not load Runs. Refresh the page to try again." />
      ) : Object.keys(groups).length === 0 ? (
        <PageEmptyState
          title="No Runs yet"
          description="When an Action runs for this brand, it will appear here."
          action={<Link
            className="font-medium text-brand-700 underline"
            to={buildBrandScopedPath("/tasks", brandId)}
          >
            Browse Actions
          </Link>}
        />
      ) : (
        Object.entries(groups).map(([label, runs]) => (
          <section key={label} className="space-y-2">
            <h2 className="text-xs font-semibold uppercase tracking-wide text-slate-500">
              {label}
            </h2>
            {runs.map((run) => {
              const outcome = runOutcome(run);
              return (
                <PageListRow
                  key={run.id}
                  title={taskRunJobLabel(run)}
                  description={outcome.description}
                  state={<Badge variant={runOutcomeBadgeVariant(outcome.state)}>{outcome.label}</Badge>}
                  metadata={timeAgo(run.started_at)}
                  workspaceAction={{
                    href: buildWorkspaceHandoffPath(`/runs/${run.id}`, brandId, "activity"),
                    label: outcome.actionLabel,
                    state: workspaceReturnLocationState("activity"),
                  }}
                />
              );
            })}
          </section>
        ))
      )}
    </NonWorkspacePage>
  );
}
